import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Activity, TrendingUp } from "lucide-react";

const AUSWERTUNG_IMAGE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_secure-family-files-1/artifacts/d0c0iddd_IMG_3589.webp";

// Musteranalyse — real product screenshot (Statusübersicht) rendered inside a
// dark phone-frame card with parallax and floating annotation chips.
export default function PatternViz() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [22, -22]);

  return (
    <div ref={ref} className="relative w-[70%] md:w-[56.25%] max-w-[520px] md:max-w-[293px] mx-auto">
      {/* Ambient cyan halo */}
      <div
        aria-hidden
        className="absolute -inset-16 md:-inset-9 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 45% at 50% 55%, rgba(0,250,248,0.22), rgba(0,250,248,0.05) 40%, transparent 70%)",
          filter: "blur(30px)",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 60, rotateX: 6 }}
        whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="relative rounded-[40px] overflow-hidden border border-white/8 bg-[#0F172A]"
        style={{
          y,
          boxShadow:
            "0 40px 120px -30px rgba(0,0,0,0.8), 0 0 60px -20px rgba(0,250,248,0.35), inset 0 0 0 1px rgba(255,255,255,0.04)",
        }}
      >
        {/* subtle grid inside frame */}
        <div className="absolute inset-0 bp-grid opacity-30" aria-hidden />

        {/* clipped screenshot */}
        <motion.img
          src={AUSWERTUNG_IMAGE}
          alt="BeParent Umgangsübersicht — Umgänge planen und bearbeiten mit Statusverteilung"
          loading="lazy"
          decoding="async"
          draggable={false}
          initial={{ scale: 1.05 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative block w-full h-auto select-none pointer-events-none"
          style={{
            filter: "brightness(0.96) contrast(1.05) saturate(0.95)",
          }}
        />

        {/* Cyan scanline sweep */}
        <motion.div
          aria-hidden
          initial={{ y: "-100%" }}
          whileInView={{ y: "120%" }}
          viewport={{ once: true }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
          className="absolute inset-x-0 h-24 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, transparent, rgba(0,250,248,0.15) 45%, rgba(0,250,248,0.35) 50%, rgba(0,250,248,0.15) 55%, transparent)",
            mixBlendMode: "screen",
          }}
        />

        {/* Corner spotlight vignette */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(45% 35% at 85% 15%, rgba(0,250,248,0.14), transparent 60%)",
          }}
        />

        {/* Bottom fade to blend */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-16 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, transparent, rgba(15,23,42,0.85))",
          }}
        />
      </motion.div>

      {/* Floating annotation chips (pattern-recognition callouts) */}
      <motion.div
        initial={{ opacity: 0, x: 24 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ delay: 0.8, duration: 0.7 }}
        className="hidden md:flex absolute -right-2 top-16 items-center gap-2 rounded-full border border-white/10 bg-[#0F172A]/90 backdrop-blur-xl px-3 py-1.5 shadow-[0_10px_30px_-10px_rgba(0,250,248,0.4)]"
      >
        <TrendingUp size={12} className="text-[#00FAF8]" />
        <span className="text-[11px] font-mono-bp text-white/85 whitespace-nowrap">
          Auffälligkeitsquote 68%
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: -24 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ delay: 1, duration: 0.7 }}
        className="hidden md:flex absolute -left-3.5 bottom-16 items-center gap-2 rounded-full border border-white/10 bg-[#0F172A]/90 backdrop-blur-xl px-3 py-1.5 shadow-[0_10px_30px_-10px_rgba(0,250,248,0.4)]"
      >
        <Activity size={12} className="text-[#00FAF8]" />
        <span className="text-[11px] font-mono-bp text-white/85 whitespace-nowrap">
          Muster erkannt · lokal
        </span>
      </motion.div>

      {/* Corner brackets (editorial crop markers) */}
      <div
        aria-hidden
        className="absolute -top-2 -left-2 w-10 h-10 md:w-6 md:h-6 pointer-events-none"
        style={{
          borderTop: "1px solid #00FAF8",
          borderLeft: "1px solid #00FAF8",
          borderTopLeftRadius: "8px",
          boxShadow: "0 0 20px rgba(0,250,248,0.4)",
        }}
      />
      <div
        aria-hidden
        className="absolute -bottom-2 -right-2 w-10 h-10 md:w-6 md:h-6 pointer-events-none"
        style={{
          borderBottom: "1px solid #00FAF8",
          borderRight: "1px solid #00FAF8",
          borderBottomRightRadius: "8px",
          boxShadow: "0 0 20px rgba(0,250,248,0.4)",
        }}
      />

      {/* Live analysis dot */}
      <div className="absolute top-4 left-4 md:top-2.5 md:left-2.5 flex items-center gap-2 rounded-full bg-black/40 backdrop-blur px-2.5 py-1 border border-white/5">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-[#00FAF8] opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00FAF8]" />
        </span>
        <span className="text-[10px] font-mono-bp uppercase tracking-widest text-white/70">
          Live · on-device
        </span>
      </div>
    </div>
  );
}
